/* eslint-disable camelcase */
import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Grid } from '@material-ui/core'
import { useForm } from 'react-hook-form'
import { ControlLabel, FormGroup } from 'react-bootstrap'
import { capitalize } from 'lodash'

import { sanitizeEmptyStringValues } from 'lib/sanitizeEmptyStringValues'
import { CONFIG } from 'shared/constants'
import { Input, Select } from 'components/forms'
import { SaveButton } from 'components/buttons'
import { SelectableOptions } from 'baby-ui/compounds/SelectableOptions/SelectableOptions'
import { StyledSubHeading } from 'components/headings'
import { saveRegistry } from 'registry/api'
import { setRegistry } from 'registry/actions'
import { useCharactersRemaining } from 'lib/hooks'
import { FLASH_MESSAGE } from '../../../shared/constants'
import { Responses } from '../../../global'

import css from './BirthdayPreferences.scss'

interface BirthdayPreferencesProps {
  onSubmit?: (data: any) => Promise<any>
  warnPreferencesLength?: number
  maxPreferencesLength?: number
}

interface Preference {
  affinity: string
  name: string
}

type OptionKeys =
  | 'clothingSizeOptions'
  | 'shoeSizeOptions'
  | 'favoriteColorOptions'

const getOptions = (key: OptionKeys) => {
  const dataOptions: Partial<Responses.Registry> =
    CONFIG?.registryAttributeDataOptions || {}
  return dataOptions[key] || []
}
export const BirthdayPreferences = ({
  onSubmit = saveRegistry,
  warnPreferencesLength = 6,
  maxPreferencesLength = 30,
}: BirthdayPreferencesProps) => {
  const {
    id,
    attributeData: {
      preferences = [],
      clothingSize = [],
      shoeSize = [],
      favoriteColor = '',
    } = {},
  } = useSelector((state: { registry: Responses.Registry }) => state.registry)

  const filterPreferences = (affinity: string, size: number) => {
    const filtered =
      preferences?.filter((pref: Preference) => pref.affinity === affinity) ||
      []
    const remainingSize = Math.max(0, size - filtered.length)

    return [
      ...filtered,
      ...Array(remainingSize).fill({ affinity, name: '' }),
    ].slice(0, size)
  }

  const clothingSizeOptions = getOptions('clothingSizeOptions')
  const shoeSizeOptions = getOptions('shoeSizeOptions')
  const favoriteColorOptions = getOptions('favoriteColorOptions')

  const loves = filterPreferences('loves', 2)
  const needs = filterPreferences('needs', 1)
  const please_no = filterPreferences('please_no', 1)

  const [submitting, setSubmitting] = useState(false)
  const dispatch = useDispatch()
  const { register, handleSubmit, setValue, watch } = useForm({
    mode: 'onChange',
    defaultValues: {
      registry: {
        id,
        registry_attributes_attributes: {
          attribute_data: {
            clothing_size: clothingSize,
            shoe_size: shoeSize,
            favorite_color: favoriteColor,
            preferences: [...loves, ...needs, ...please_no],
          },
        },
      },
    },
  })

  const generateHelpText = (charRemaining: number) =>
    charRemaining <= warnPreferencesLength
      ? `${charRemaining} characters remaining`
      : ''

  const lovesRemaining = useCharactersRemaining({
    value: watch(
      'registry[registry_attributes_attributes][attribute_data][preferences][0][name]'
    ),
    maxCharacters: maxPreferencesLength,
  })

  const andRemaining = useCharactersRemaining({
    value: watch(
      'registry[registry_attributes_attributes][attribute_data][preferences][1][name]'
    ),
    maxCharacters: maxPreferencesLength,
  })

  const needsRemaining = useCharactersRemaining({
    value: watch(
      'registry[registry_attributes_attributes][attribute_data][preferences][2][name]'
    ),
    maxCharacters: maxPreferencesLength,
  })

  const pleaseNoRemaining = useCharactersRemaining({
    value: watch(
      'registry[registry_attributes_attributes][attribute_data][preferences][3][name]'
    ),
    maxCharacters: maxPreferencesLength,
  })

  const lovesHelpText = generateHelpText(lovesRemaining)
  const andHelpText = generateHelpText(andRemaining)
  const needsHelpText = generateHelpText(needsRemaining)
  const pleaseNoHelpText = generateHelpText(pleaseNoRemaining)

  const dispatchSubmit = (data: any) => {
    setSubmitting(true)
    onSubmit(sanitizeEmptyStringValues(data))
      .then(({ registry }) => {
        setSubmitting(false)
        dispatch(setRegistry(registry))
        PubSub.publish(FLASH_MESSAGE, {
          message: 'Your registry info has been updated!',
          variant: 'success',
        })
      })
      .catch(({ error: { message } }) => {
        PubSub.publish(FLASH_MESSAGE, {
          message,
          variant: 'error',
        })

        setSubmitting(false)
      })
  }

  return (
    <>
      <StyledSubHeading>Preferences</StyledSubHeading>
      <form onSubmit={handleSubmit(dispatchSubmit)}>
        <Grid container spacing={2}>
          <input
            name="registry[id]"
            ref={register({ required: true })}
            type="hidden"
          />

          <Grid item xs={12}>
            <FormGroup>
              <ControlLabel htmlFor="clothing_size">Clothing size</ControlLabel>
              <input
                id="clothing_size"
                name="registry[registry_attributes_attributes][attribute_data][clothing_size][0]"
                ref={register()}
                type="hidden"
              />
              <SelectableOptions
                options={clothingSizeOptions}
                selectedOptions={clothingSize}
                onSelect={([pickedSize]) => {
                  setValue(
                    'registry[registry_attributes_attributes][attribute_data][clothing_size][0]',
                    pickedSize
                  )
                }}
              />
            </FormGroup>
          </Grid>

          <Grid item xs={12}>
            <FormGroup>
              <ControlLabel>Shoe size</ControlLabel>
              <input
                name="registry[registry_attributes_attributes][attribute_data][shoe_size][0][group]"
                ref={register()}
                type="hidden"
              />
              <input
                name="registry[registry_attributes_attributes][attribute_data][shoe_size][0][size]"
                ref={register()}
                type="hidden"
              />
              <SelectableOptions
                options={shoeSizeOptions}
                selectedOptions={shoeSize}
                onSelect={([pickedSize]) => {
                  if (typeof pickedSize === 'string') return

                  if (pickedSize === undefined) {
                    setValue(
                      'registry[registry_attributes_attributes][attribute_data][shoe_size][0][group]',
                      null
                    )
                    setValue(
                      'registry[registry_attributes_attributes][attribute_data][shoe_size][0][size]',
                      null
                    )
                  } else {
                    const { group, size } = pickedSize

                    setValue(
                      'registry[registry_attributes_attributes][attribute_data][shoe_size][0][group]',
                      group
                    )
                    setValue(
                      'registry[registry_attributes_attributes][attribute_data][shoe_size][0][size]',
                      size
                    )
                  }
                }}
              />
            </FormGroup>
          </Grid>

          <Grid item xs={12}>
            <Input
              id="loves"
              inputRef={register()}
              label="Loves"
              maxLength={maxPreferencesLength}
              name="registry[registry_attributes_attributes][attribute_data][preferences][0][name]"
            />
            {lovesHelpText && (
              <span className={css.helpText}>{lovesHelpText}</span>
            )}
          </Grid>
          <input
            defaultValue="loves"
            name="registry[registry_attributes_attributes][attribute_data][preferences][0][affinity]"
            ref={register()}
            type="hidden"
          />
          <Grid item xs={12}>
            <Input
              id="and"
              inputRef={register()}
              maxLength={maxPreferencesLength}
              name="registry[registry_attributes_attributes][attribute_data][preferences][1][name]"
            />
            {andHelpText && <span className={css.helpText}>{andHelpText}</span>}
          </Grid>
          <input
            defaultValue="loves"
            name="registry[registry_attributes_attributes][attribute_data][preferences][1][affinity]"
            ref={register()}
            type="hidden"
          />

          <Grid item xs={12}>
            <Input
              id="needs"
              inputRef={register()}
              label="Needs"
              maxLength={maxPreferencesLength}
              name="registry[registry_attributes_attributes][attribute_data][preferences][2][name]"
            />
            {needsHelpText && (
              <span className={css.helpText}>{needsHelpText}</span>
            )}
          </Grid>
          <input
            defaultValue="needs"
            name="registry[registry_attributes_attributes][attribute_data][preferences][2][affinity]"
            ref={register()}
            type="hidden"
          />

          <Grid item xs={12}>
            <Select
              id="favorite_color"
              inputRef={register()}
              label="Favorite color"
              name="registry[registry_attributes_attributes][attribute_data][favorite_color]"
            >
              {favoriteColorOptions.map(
                ({
                  name,
                  hex: _hex,
                }: {
                  name: string
                  hex: string | string[]
                }) => (
                  <option key={name} value={name}>
                    {capitalize(name)}
                  </option>
                )
              )}
            </Select>
          </Grid>

          <Grid item xs={12}>
            <Input
              id="please_no"
              inputRef={register()}
              label="Please no"
              name="registry[registry_attributes_attributes][attribute_data][preferences][3][name]"
            />
            {pleaseNoHelpText && (
              <span className={css.helpText}>{pleaseNoHelpText}</span>
            )}
          </Grid>
          <input
            defaultValue="please_no"
            name="registry[registry_attributes_attributes][attribute_data][preferences][3][affinity]"
            ref={register()}
            type="hidden"
          />

          <Grid item xs={12}>
            <SaveButton className="" submitting={submitting}>
              Save
            </SaveButton>
          </Grid>
        </Grid>
      </form>
    </>
  )
}
